import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AnalysisObject } from "@repo/shared";

/**
 * Object ID -> title of the sheet it renders on. `null` means the object exists
 * but sits on no sheet (orphan); a missing entry means the reference is not a
 * screen object — master items are absent from the object inventory, so they
 * resolve to undefined and render with no sheet at all.
 */
export function useSheetTitles(objects: AnalysisObject[]): Map<string, string | null> {
  const { t } = useTranslation("analyses");
  return useMemo(() => {
    const sheetTitles = new Map<string, string>();
    for (const obj of objects) {
      if (obj.type === "sheet") sheetTitles.set(obj.id, obj.title);
    }

    const byObjectId = new Map<string, string | null>();
    for (const obj of objects) {
      if (obj.type === "sheet") continue;
      // Sheets do carry titles, so an id here is the last resort — label it as
      // one instead of passing a raw id off as the sheet's name.
      byObjectId.set(
        obj.id,
        obj.sheetId
          ? sheetTitles.get(obj.sheetId) || t("usageRefs.unknownSheet", { id: obj.sheetId })
          : null,
      );
    }
    return byObjectId;
  }, [objects, t]);
}
